// WbTicketRightRail — Phase C10 Group 4.
//
// Right rail of the New Ticket page. Holds:
//   • Pricing summary card (lines + subtotal/VAT/grand total). Lines with
//     a fresh resolved-price override use the resolved line_total in
//     preference to the persisted one (server is canonical pricer; a
//     stale ticket.line.line_total can still be on the wire after a
//     resolve has changed inputs but autosave hasn't landed yet).
//   • Weight summary card — mode-aware visibility. Hidden entirely for
//     units_only; shown only after the relevant weight(s) are captured
//     for the other three modes.
//   • Snapshots thumbnail grid (read-only — the on-prem agent uploads
//     images via POST /wb/tickets/:enc_id/snapshots; the rail just renders
//     them via /files/presigned in the same pattern as WbTicketDetailPage
//     does).
//   • Actions card — primary "Complete ticket" button + a placeholder
//     "Barrier controls" card with two disabled buttons (the agent-
//     protocol that actually opens/closes barriers lands in a later
//     phase).
//
// The Complete-ticket modal lives in WbCompleteTicketModal.tsx — opening
// it is the only thing this rail does on the button click; the modal owns
// the form state, the POST, the toast and the navigation.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../../components/ui/Card/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { Badge } from '../../../../components/ui/Badge/Badge';
import { Spinner } from '../../../../components/ui/Spinner/Spinner';
import { apiClient } from '../../../../api/client';
import { endpoints } from '../../../../api/endpoints';
import { extractErrorMessage } from '../../../../utils/errorMessage';
import { useToastStore } from '../../../../store/toastStore';
import { useWbTicket } from '../../context/WbTicketContext';
import { WbCompleteTicketModal } from './WbCompleteTicketModal';
import { WbSnapshotThumb } from '../../components/WbSnapshotThumb';
import type {
  TicketDetailDto,
  WeighingMode,
  WeightMethod,
} from '../../hooks/useWbTicket';
import styles from './WbTicketRightRail.module.css';

// -- Formatters -----------------------------------------------------------

const GBP = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
});

function fmtCurrency(n: number): string {
  return GBP.format(n);
}

function fmtKg(n: number | null): string {
  if (n === null || n === undefined) return '—';
  return `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(n)} kg`;
}

const METHOD_LABEL: Record<WeightMethod, string> = {
  hardware: 'Hardware',
  manual: 'Manual',
  stored: 'Stored',
};

function methodBadge(method: WeightMethod | null): JSX.Element {
  if (method === null) return <span className={styles.muted}>—</span>;
  return <Badge variant="muted">{METHOD_LABEL[method]}</Badge>;
}

// -- Weight summary visibility -------------------------------------------
//
// Mirrors the per-mode rules in the brief:
//   two_pass      → need BOTH gross + tare.
//   stored_tare   → need gross (the tare comes from the vehicle record).
//   manual_tare   → need BOTH gross + tare.
//   units_only    → never render.
function shouldShowWeightSummary(
  mode: WeighingMode,
  grossKg: number | null,
  tareKg: number | null,
): boolean {
  if (mode === 'units_only') return false;
  if (mode === 'two_pass' || mode === 'manual_tare') {
    return grossKg !== null && tareKg !== null;
  }
  if (mode === 'stored_tare') {
    return grossKg !== null;
  }
  return false;
}

// -- Complete-ticket enable rule -----------------------------------------
//
// Same mode-aware weight requirements as the weight-summary card, with
// the extra precondition that the ticket is still in a "writable" status
// (draft / open) and has at least one line.
function canCompleteTicket(ticket: TicketDetailDto): boolean {
  if (ticket.status !== 'draft' && ticket.status !== 'open') return false;
  if (ticket.lines.length === 0) return false;
  // A customer quick-created at the kiosk (is_complete=false) can't be billed
  // until its record is filled in on the main module — the server returns 422
  // (GATE 2.5). Mirror that here so the operator gets a disabled button + a
  // clear reason rather than a failed request. Only an explicit `false`
  // blocks (undefined on older payloads/fixtures means "complete").
  if (ticket.customer_is_complete === false) return false;
  const { weighing_mode, gross_weight_kg, tare_weight_kg } = ticket;
  if (weighing_mode === 'units_only') return true;
  if (weighing_mode === 'stored_tare') {
    return gross_weight_kg !== null;
  }
  // two_pass + manual_tare both need both readings.
  return gross_weight_kg !== null && tare_weight_kg !== null;
}

// -- Pricing summary card -------------------------------------------------

interface PricingSummaryCardProps {
  ticket: TicketDetailDto | null;
}

function PricingSummaryCard({ ticket }: PricingSummaryCardProps): JSX.Element {
  const { resolvedPrices, resolvingPriceForLine } = useWbTicket();

  if (ticket === null) {
    return (
      <Card title="Pricing summary">
        <p className={styles.muted}>
          Awaiting ticket creation — pick a customer in the left rail.
        </p>
      </Card>
    );
  }

  const calculating = resolvingPriceForLine.size > 0;

  // Totals: on a draft the server hasn't priced the ticket (subtotal /
  // vat_total / total are 0 until completeTicket), so derive them client-side
  // from the resolved per-line prices. Completed tickets are read-only and
  // carry canonical server totals — trust those.
  const isCompleted = ticket.status === 'completed';
  const computed = ticket.lines.reduce(
    (acc, line) => {
      const resolved =
        line.uid != null ? resolvedPrices[line.uid] : undefined;
      const exVat = resolved?.subtotal_ex_vat ?? line.subtotal ?? 0;
      const incVat = resolved?.subtotal_inc_vat ?? line.line_total ?? 0;
      acc.subtotal += exVat;
      acc.vat += incVat - exVat;
      acc.total += incVat;
      return acc;
    },
    { subtotal: 0, vat: 0, total: 0 },
  );
  const subtotal = isCompleted ? ticket.subtotal : computed.subtotal;
  const vatTotal = isCompleted ? ticket.vat_total : computed.vat;
  const grandTotal = isCompleted ? ticket.total : computed.total;

  return (
    <Card title="Pricing summary">
      {ticket.lines.length === 0 ? (
        <p className={styles.muted}>No lines yet.</p>
      ) : (
        <div data-testid="wb-right-rail-lines">
          {ticket.lines.map((line, idx) => {
            // Prefer the fresh resolve's subtotal_inc_vat when present — it
            // represents the latest server-side pricing for this line. The
            // line carries its stable uid through the optimistic projection,
            // so we key resolvedPrices by it directly (no positional bridge
            // to drift after add/remove). Server-echoed lines have no uid and
            // fall back to the canonical line_total.
            const resolved =
              line.uid != null ? resolvedPrices[line.uid] : undefined;
            const lineTotal =
              resolved?.subtotal_inc_vat ?? line.line_total;
            const productName =
              line.product?.name ??
              line.product_name_snapshot ??
              'Custom line';
            return (
              <div
                key={line.id ?? idx}
                className={styles.lineRow}
                data-testid={`wb-right-rail-line-${idx}`}
              >
                <span className={styles.lineLabel}>
                  {productName} × {line.quantity}
                </span>
                <span className={styles.lineValue}>
                  {fmtCurrency(lineTotal)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {calculating ? (
        <div
          className={styles.calculating}
          data-testid="wb-right-rail-calculating"
        >
          <Spinner size="sm" label="Calculating" />
          <span>Calculating…</span>
        </div>
      ) : (
        <div className={styles.totalsBlock}>
          <div className={styles.totalRow}>
            <span>Subtotal</span>
            <span className={styles.totalValue}>
              {fmtCurrency(subtotal)}
            </span>
          </div>
          <div className={styles.totalRow}>
            <span>VAT total</span>
            <span className={styles.totalValue}>
              {fmtCurrency(vatTotal)}
            </span>
          </div>
          <div className={styles.grandRow}>
            <span className={styles.grandLabel}>Grand total</span>
            <span
              className={styles.grandValue}
              data-testid="wb-right-rail-grand-total"
            >
              {fmtCurrency(grandTotal)}
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}

// -- Weight summary card -------------------------------------------------

interface WeightSummaryCardProps {
  ticket: TicketDetailDto;
}

function WeightSummaryCard({ ticket }: WeightSummaryCardProps): JSX.Element {
  // Net falls back to gross - tare when the server hasn't denormalised the
  // net_weight_kg column yet (mid-flow drafts often haven't).
  const netComputed =
    ticket.net_weight_kg ??
    (ticket.gross_weight_kg !== null && ticket.tare_weight_kg !== null
      ? ticket.gross_weight_kg - ticket.tare_weight_kg
      : null);

  return (
    <Card title="Weight summary">
      <div className={styles.weights}>
        <div className={styles.weightRow}>
          <span className={styles.weightLabel}>Gross</span>
          <span className={styles.weightValue}>
            {fmtKg(ticket.gross_weight_kg)}
          </span>
          {methodBadge(ticket.gross_method)}
        </div>
        <div className={styles.weightRow}>
          <span className={styles.weightLabel}>Tare</span>
          <span className={styles.weightValue}>
            {fmtKg(ticket.tare_weight_kg)}
          </span>
          {methodBadge(ticket.tare_method)}
        </div>
        <div className={styles.weightRow}>
          <span className={styles.weightLabel}>Net</span>
          <span className={styles.weightValue}>{fmtKg(netComputed)}</span>
        </div>
      </div>
    </Card>
  );
}

// -- Snapshots card ------------------------------------------------------

interface SnapshotsCardProps {
  ticket: TicketDetailDto;
}

function SnapshotsCard({ ticket }: SnapshotsCardProps): JSX.Element {
  if (ticket.snapshots.length === 0) {
    return (
      <Card title="Snapshots">
        <p className={styles.muted}>No snapshots yet.</p>
      </Card>
    );
  }
  return (
    <Card title="Snapshots">
      <div className={styles.snapshotGrid}>
        {ticket.snapshots.map((s) => (
          <WbSnapshotThumb key={s.id} scopeKey={ticket.enc_id} snapshot={s} />
        ))}
      </div>
    </Card>
  );
}

// -- Right rail composer -------------------------------------------------

export function WbTicketRightRail(): JSX.Element {
  const { ticket, isDirty, isSaving, saveTicket, resetTicket } = useWbTicket();
  const navigate = useNavigate();
  const pushToast = useToastStore((s) => s.push);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const showWeightSummary =
    ticket !== null &&
    shouldShowWeightSummary(
      ticket.weighing_mode,
      ticket.gross_weight_kg,
      ticket.tare_weight_kg,
    );

  const completeEnabled = ticket !== null && canCompleteTicket(ticket);
  const isDraft = ticket?.status === 'draft';

  function goToList(): void {
    resetTicket();
    navigate('/weighbridge/tickets');
  }

  async function handleSave(): Promise<void> {
    const ok = await saveTicket();
    if (!ok) return;
    setJustSaved(true);
    window.setTimeout(() => setJustSaved(false), 2000);
  }

  function handleClose(): void {
    // Navigate away directly — the editor's unmount cleanup releases the lock
    // (migration 167) and the debounced autosave has already persisted edits.
    // No confirmation needed; the lock is the concurrent-edit signal.
    goToList();
  }

  async function handleDelete(): Promise<void> {
    if (ticket === null) return;
    const ok = window.confirm(
      'Delete this draft ticket? This cannot be undone.',
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await apiClient.delete(endpoints.wb.ticket(ticket.enc_id));
      goToList();
    } catch (err) {
      pushToast({ type: 'error', message: extractErrorMessage(err) });
      setDeleting(false);
    }
  }

  return (
    <aside className={styles.rail} data-testid="wb-right-rail">
      <PricingSummaryCard ticket={ticket} />

      {showWeightSummary && ticket !== null ? (
        <WeightSummaryCard ticket={ticket} />
      ) : null}

      {ticket !== null ? <SnapshotsCard ticket={ticket} /> : null}

      <Card title="Actions">
        <div className={styles.actions}>
          {ticket !== null && ticket.customer_is_complete === false ? (
            <div role="alert" data-testid="wb-customer-incomplete-warning" className={styles.incompleteWarning}>
              ⚠ Customer incomplete — this ticket can’t be completed until the
              customer’s contact &amp; address details are finished in the
              customer record.
            </div>
          ) : null}
          <Button
            variant="primary"
            onClick={() => setCompleteOpen(true)}
            disabled={!completeEnabled}
            data-testid="wb-right-rail-complete"
          >
            Complete ticket
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              void handleSave();
            }}
            disabled={ticket === null || !isDirty || isSaving}
            data-testid="wb-right-rail-save"
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
          {justSaved ? (
            <span className={styles.muted} data-testid="wb-right-rail-saved">
              Saved
            </span>
          ) : null}
          <Button
            variant="ghost"
            onClick={handleClose}
            data-testid="wb-right-rail-close"
          >
            Close
          </Button>
          {isDraft ? (
            <Button
              variant="danger"
              onClick={() => {
                void handleDelete();
              }}
              disabled={deleting}
              data-testid="wb-right-rail-delete"
            >
              {deleting ? 'Deleting…' : 'Delete ticket'}
            </Button>
          ) : null}
        </div>
      </Card>

      <Card title="Barrier controls">
        <div className={styles.actions}>
          <Button
            variant="secondary"
            disabled
            title="Coming in agent-protocol phase"
            data-testid="wb-right-rail-barrier-open"
          >
            Open barrier
          </Button>
          <Button
            variant="secondary"
            disabled
            title="Coming in agent-protocol phase"
            data-testid="wb-right-rail-barrier-close"
          >
            Close barrier
          </Button>
        </div>
      </Card>

      {ticket !== null ? (
        <WbCompleteTicketModal
          open={completeOpen}
          onClose={() => setCompleteOpen(false)}
          ticket={ticket}
        />
      ) : null}
    </aside>
  );
}
